import React, { useState, useEffect } from "react";
import {
  FaGavel,
  FaUser,
  FaPoll,
  FaBars,
} from "react-icons/fa";
import { RiDashboardFill, RiSettingsLine } from "react-icons/ri";
import { BiSolidUserBadge, BiCategory } from "react-icons/bi";
import { LiaUserLockSolid } from "react-icons/lia";
import { IoClose } from "react-icons/io5";
import { LuArrowLeftToLine } from "react-icons/lu";
import { MdExpandMore } from "react-icons/md";
import Logo from "../../../assets/icons/logo.svg";
import { Link, useLocation } from "react-router-dom";
import useAuthStore from "../../../store/authStore";

const Sidebar = ({ isCollapsed, onToggleCollapse, closeSidebar, isMobileOpen }) => {
  const [activeItem, setActiveItem] = useState(null);
  const [activeSubMenuItem, setActiveSubMenuItem] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const location = useLocation();
  const { userType } = useAuthStore();

  const menuItems = [
    { name: "Dashboard", icon: <RiDashboardFill className="text-[20px]" />, path: "/" },
    { name: "Bids", icon: <FaGavel className="text-[20px]" />, path: "/bid-list" },
    {
      name: "Buyers",
      icon: <FaUser className="text-[20px]" />,
      subItems: [
        { name: "Individual", path: "/buyer-list" },
        { name: "Group", path: "/group-list" },
      ],
    },
    {
      name: "Surveys",
      icon: <FaPoll className="text-[20px]" />,
      subItems: [
        { name: "Survey List", path: "/survey-list" },
        { name: "Surveyors", path: "/surveyor-list" },
        { name: "Survey Settlement", path: "/survey-settlement" },
      ],
    },
    { name: "Manage Content", icon: <BiSolidUserBadge className="text-[20px]" />, path: "/manage-content" },
    { name: "Managers", icon: <LiaUserLockSolid className="text-[20px]" />, path: "/manager-list" },
    ...(userType === "admin" ? [{ name: "Categories", icon: <BiCategory className="text-[20px]" />, path: "/category" }] : []),
    { name: "Settings", icon: <RiSettingsLine className="text-[20px]" />, path: "/setting" },
  ];

  useEffect(() => {
    const currentPath = location.pathname;

    const activeMenuItem = menuItems.find(item => item.path === currentPath);

    const activeSubItem = menuItems
      .flatMap(item => item.subItems || [])
      .find(subItem => subItem.path === currentPath);

    if (activeSubItem) {
      setActiveSubMenuItem(activeSubItem.name);
      setActiveItem(
        menuItems.find(item => item.subItems && item.subItems.some(sub => sub.path === activeSubItem.path))?.name
      );
      setOpenSubMenu(
        menuItems.find(item => item.subItems && item.subItems.some(sub => sub.path === activeSubItem.path))?.name
      );
    } else if (activeMenuItem) {
      setActiveItem(activeMenuItem.name);
      setOpenSubMenu(activeMenuItem.subItems ? activeMenuItem.name : null);
    } else {
      setActiveItem(null);
      setActiveSubMenuItem(null);
    }
  }, [location.pathname]);

  const handleItemClick = (item) => {
    setActiveItem(item.name);
    if (item.subItems) {
      setOpenSubMenu(openSubMenu === item.name ? null : item.name);
    } else {
      setOpenSubMenu(null);
      if (closeSidebar) closeSidebar();
    }
    setActiveSubMenuItem(null);
  };

  const handleSubItemClick = (subItem) => {
    setActiveSubMenuItem(subItem.name);
    if (closeSidebar) closeSidebar();
  };

  return (
    <div
      className={`flex flex-col h-full bg-white transition-all duration-300 border-r border-[#9FACF0] ${isCollapsed ? "lg:w-20" : "lg:w-64"
        } w-64 overflow-hidden shadow-2xl lg:shadow-none`}
      style={{ backgroundColor: "#ffffff" }}
    >
      <div className="p-4 flex justify-between items-center min-h-[80px]">
        {(!isCollapsed || isMobileOpen) && (
          <img src={Logo} alt="logo" className="h-10 mt-2" />
        )}
        <button
          className="lg:hidden text-indigo-700"
          onClick={closeSidebar}
        >
          <IoClose className="text-[25px]" />
        </button>
      </div>

      <nav className="flex-1 px-4 overflow-y-auto scrollbar-hide">
        <ul className="space-y-2">
          {menuItems.map((item) => (
            <li key={item.name}>
              <div>
                <Link
                  to={item.path || "#"}
                  className={`flex items-center justify-between text-[16px] px-[16px] py-[12px] rounded-md transition-colors ${activeItem === item.name
                    ? "bg-[#1955EB] text-white"
                    : "text-[#9FACF0] hover:bg-gray-50 hover:text-[#1955EB]"
                    }`}
                  onClick={() => handleItemClick(item)}
                >
                  <span className="flex items-center">
                    <span className="mr-3">{item.icon}</span>
                    {(!isCollapsed || isMobileOpen) && (
                      <span className="font-medium">{item.name}</span>
                    )}
                  </span>
                  {item.subItems && (!isCollapsed || isMobileOpen) && (
                    <MdExpandMore
                      className={`transform transition-transform text-[20px] ${openSubMenu === item.name ? "rotate-180" : ""
                        }`}
                    />
                  )}
                </Link>
                {item.subItems && openSubMenu === item.name && (!isCollapsed || isMobileOpen) && (
                  <ul className="mt-1 ml-4 space-y-1 border-l-2 border-[#D2DEFF]">
                    {item.subItems.map((subItem) => (
                      <li key={subItem.name}>
                        <Link
                          to={subItem.path}
                          className={`block px-4 py-2 text-[14px] rounded-r-md transition-colors ${activeSubMenuItem === subItem.name
                            ? "text-[#1955EB] font-bold bg-[#F0F5FF]"
                            : "text-[#9FACF0] hover:text-[#1955EB] hover:bg-gray-50"
                            }`}
                          onClick={() => handleSubItemClick(subItem)}
                        >
                          {subItem.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>
      </nav>

      <div className="p-4 border-t border-[#F0F5FF]">
        <button
          className="w-full bg-[#9FACF0]/10 text-[#1955EB] hover:bg-[#9FACF0]/20 transition-colors py-2 px-4 rounded-md flex items-center justify-center lg:flex hidden"
          onClick={onToggleCollapse}
        >
          <LuArrowLeftToLine
            className={`transform transition-transform ${isCollapsed ? "rotate-180" : "rotate-0"
              }`}
          />
          {!isCollapsed && <span className="ml-2 font-medium">Collapse</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
